import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Download } from "lucide-react";
import { IMAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });

export default function SignContract() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [contract, setContract] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/contracts/${id}`, { params: { token } })
      .then(({ data }) => setContract({ ...data, token }))
      .catch(() => setError("We couldn't find this agreement. Please use the link from your email."));
  }, [id, token]);

  const sign = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2 || !agree) {
      toast.error("Please type your full name and tick the box to agree.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contracts/${id}/sign`, { token, signed_name: name, agree });
      setContract({ ...contract, status: "signed", ...data });
      toast.success("Thank you — your agreement is signed. A copy is on its way to your inbox.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not sign. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-body px-6 py-14" style={{ backgroundColor: "var(--bg-secondary)" }}>
      <div
        data-testid="contract-page"
        className="mx-auto max-w-2xl rounded-3xl p-8 md:p-12 shadow-xl"
        style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}
      >
        <img src={IMAGES.logo} alt="ProExec Virtual Solutions" className="h-14 w-auto mx-auto mb-6" />
        {error && <p data-testid="contract-error" className="text-center" style={{ color: "var(--text-secondary)" }}>{error}</p>}
        {!error && !contract && <Loader2 className="animate-spin mx-auto" color="var(--rose-mocha)" />}
        {contract && <ContractBody contract={contract} />}
        {contract?.status === "signed" && <SignedBadge contract={contract} />}
        {contract && contract.status !== "signed" && (
          <form onSubmit={sign} data-testid="contract-sign-form" className="mt-8 flex flex-col gap-5">
            <label className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>
              Type your full name to sign
            </label>
            <input
              data-testid="contract-signature-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={contract.name}
              className="bg-transparent py-2 outline-none font-serif italic text-3xl"
              style={{ borderBottom: "1px solid var(--mocha-brown)", color: "var(--text-primary)" }}
            />
            <label className="flex items-start gap-3 text-sm cursor-pointer" style={{ color: "var(--text-secondary)" }}>
              <input
                type="checkbox"
                data-testid="contract-agree-checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-1 accent-[#4A3B32]"
              />
              I have read and agree to the terms above, and I understand that typing my name serves as my electronic signature.
            </label>
            <button
              type="submit"
              data-testid="contract-sign-button"
              disabled={loading}
              className="self-start rounded-full px-8 py-3.5 text-sm font-medium disabled:opacity-60"
              style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
            >
              {loading ? "Signing…" : "Sign agreement"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ContractBody({ contract }) {
  return (
    <>
      <h1 className="font-serif text-3xl md:text-4xl text-center mb-2" style={{ color: "var(--text-primary)" }}>
        Service Agreement
      </h1>
      <p className="text-center text-sm mb-8" style={{ color: "var(--text-secondary)" }}>
        Between {contract.name} and ProExec Virtual Solutions
      </p>
      <ul data-testid="contract-terms" className="flex flex-col gap-3 list-disc pl-5" style={{ color: "var(--text-secondary)" }}>
        {contract.terms.map((t) => (
          <li key={t} dangerouslySetInnerHTML={{ __html: t }} />
        ))}
      </ul>
    </>
  );
}

function SignedBadge({ contract }) {
  return (
    <div data-testid="contract-signed-badge" className="mt-8 rounded-2xl p-5 flex items-start gap-3" style={{ backgroundColor: "#F3DAD5" }}>
      <CheckCircle2 color="var(--mocha-brown)" className="shrink-0 mt-1" />
      <div>
        <p className="font-serif italic text-2xl" style={{ color: "var(--text-primary)" }}>{contract.signed_name}</p>
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Signed {fmt(contract.signed_at)}</p>
        <a
          data-testid="contract-download-pdf"
          href={`${API}/contracts/${contract.id}/pdf?token=${contract.token}`}
          className="inline-flex items-center gap-2 mt-3 rounded-full px-5 py-2 text-sm"
          style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}
        >
          <Download size={14} /> Download PDF
        </a>
      </div>
    </div>
  );
}
